/**
 * What borrowing against the home actually does — stated wherever a strategy does it.
 *
 * OWNER-RAISED, 2026-09-01, of a card titled "Borrow $800k against the home for 100% of the room,
 * keep the home" sitting under Optimal Cashflow:
 *
 *   "At first I was looking at the cashflow to see the loan repayments and it wasn't until I clicked
 *    on 'Explain this' that I realised that this is a reverse mortgage. Where a reverse mortgage is
 *    used this should be called out in the title. Also the explainer needs to clearly outline the
 *    implications of this product type — it's literally silent on this matter which is extremely
 *    dangerous!"
 *
 * All of that was true. THREE separate failures compounded:
 *
 *   1. The title never said "reverse mortgage". It said "borrow against the home", which reads like
 *      an ordinary loan.
 *   2. THERE ARE NO REPAYMENTS, so nothing appeared in the cashflow — and the absence of a cost line
 *      is exactly what made the strategy top the cashflow ranking. A reader looking for the cost of
 *      the loan finds nothing, and the natural conclusion is that there isn't one.
 *   3. The candidate builder wrote a careful `equityReleaseRationale` for every one of these
 *      strategies explaining the compounding and the trade-off — and NOTHING RENDERED IT. It was
 *      written and thrown away.
 *
 * The interest is real, it compounds at roughly 8% with nothing paid along the way, and it is
 * settled out of the house. This component says so, with the projected debt rather than a warning in
 * the abstract, because the figure is what makes it concrete.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';

export type EquityReleaseKind = 'reverseMortgage' | 'heas';

export interface EquityReleaseTermsProps {
    kind: EquityReleaseKind;
    /** What is drawn against the home. For HEAS, the total drawn over the projection. */
    loanAmount?: number | null;
    /** The annual interest rate, as a percentage. */
    interestRate?: number | null;
    /** What the debt is projected to reach by the end of the horizon. */
    projectedBalance?: number | null;
    /** The horizon that balance was measured over. */
    years?: number | null;
    /** The home's value today, so the debt can be put in proportion. */
    homeValue?: number | null;
    className?: string;
}

const LABEL: Record<EquityReleaseKind, string> = {
    reverseMortgage: 'reverse mortgage',
    heas: 'Home Equity Access Scheme loan',
};

/**
 * The loan's terms, and what it grows to.
 *
 * Written the same way as the annuity card: one plain sentence carrying the whole answer, with the
 * detail underneath for the reader who wants it.
 */
export function EquityReleaseTerms({
    kind,
    loanAmount,
    interestRate,
    projectedBalance,
    years,
    homeValue,
    className = '',
}: EquityReleaseTermsProps) {
    const [explain, setExplain] = useState(false);
    const borrowed = Math.max(0, Number(loanAmount) || 0);
    if (!(borrowed > 0)) return null;

    const rate = Number(interestRate) || 0;
    const grown = Math.max(0, Number(projectedBalance) || 0);
    const horizon = Math.max(0, Number(years) || 0);
    const interestAdded = grown > borrowed ? grown - borrowed : 0;
    const home = Math.max(0, Number(homeValue) || 0);
    const shareOfHome = home > 0 && grown > 0 ? Math.round((grown / home) * 100) : null;

    return (
        <>
            <div className={`rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white p-4 ${className}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                    This plan borrows against the home
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{formatCurrency(borrowed)}</span> is drawn against the
                    family home as a <span className="font-semibold text-slate-900">{LABEL[kind]}</span>
                    {rate > 0 && <> at <span className="font-semibold text-slate-900">{rate}% a year</span></>}.{' '}
                    {/*
                      * THE SENTENCE THE OWNER WENT LOOKING FOR AND COULD NOT FIND. No repayments is
                      * the whole reason this strategy tops a cashflow ranking, and the reason nothing
                      * about the loan appears in the cashflow figures. Said first, and plainly.
                      */}
                    <span className="font-semibold text-slate-900">Nothing is repaid along the way</span> — which is why
                    no loan repayment appears in the cashflow above. The interest is added to the loan each year and the
                    whole debt is settled out of the house.
                </p>

                {interestAdded > 0 && horizon > 0 && (
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                        Over {horizon} {horizon === 1 ? 'year' : 'years'} the debt grows from{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(borrowed)}</span> to{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(grown)}</span> —{' '}
                        <span className="font-semibold text-slate-900">{formatCurrency(interestAdded)} of interest</span>{' '}
                        that comes out of the estate
                        {shareOfHome != null && <>, or about {shareOfHome}% of the home&apos;s value today</>}. It keeps
                        compounding for as long as the loan runs, so a longer stay costs considerably more.
                    </p>
                )}

                <button
                    type="button"
                    onClick={() => setExplain(true)}
                    className="mt-2.5 text-[12px] font-semibold text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:text-blue-900"
                >
                    What is a {LABEL[kind]}, and what are the risks?
                </button>
            </div>
            <EquityReleaseExplainerModal kind={kind} open={explain} onClose={() => setExplain(false)} />
        </>
    );
}

/**
 * Plain words on equity release, written for someone meeting it inside a recommendation.
 *
 * The risks are given more room than the benefits, deliberately. A reverse mortgage is the single
 * most consequential product this tool can propose — it is secured on the family home, it cannot be
 * undone cheaply, and its cost is invisible until the house is sold.
 */
export function EquityReleaseExplainerModal({
    kind,
    open,
    onClose,
}: {
    kind: EquityReleaseKind;
    open: boolean;
    onClose: () => void;
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;
    const isHeas = kind === 'heas';

    return createPortal(
        /* z-70: this opens from inside "Understanding your plan", which is itself a modal. */
        <div
            className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-brand-ink/55 p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="equity-release-title"
            onClick={onClose}
        >
            <div
                className="my-4 w-full max-w-2xl rounded-2xl border border-slate-200/70 bg-white p-6 shadow-cardlg sm:p-7"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h3 id="equity-release-title" className="text-[23px] font-bold leading-tight">
                            <span className="text-brand-navy ">
                                {isHeas ? 'What is the Home Equity Access Scheme?' : 'What is a reverse mortgage?'}
                            </span>
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-600">
                            {isHeas
                                ? 'A government loan against your home, repaid from your estate.'
                                : 'A loan against your home that you never make repayments on.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="mt-1 shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                        <X size={20} strokeWidth={2.4} />
                    </button>
                </div>

                <div className="space-y-3 text-[15px] leading-relaxed text-gray-700">
                    <p>
                        {isHeas ? (
                            <>
                                The Home Equity Access Scheme is a <strong>government loan</strong> secured against your
                                home. Services Australia pays you a regular amount (or a lump sum), you make no
                                repayments, and the loan plus interest is repaid when the home is sold or from your
                                estate. The rate is well below a commercial reverse mortgage, which is what makes it
                                worth modelling first.
                            </>
                        ) : (
                            <>
                                A reverse mortgage is a loan secured against your home. You receive the money now and{' '}
                                <strong>make no repayments at all</strong>. Each year the interest is added to what you
                                owe, and the whole debt is repaid when the home is sold — usually when you die or move
                                into care permanently.
                            </>
                        )}
                    </p>

                    {/*
                      * THE COMPOUNDING, GIVEN ITS OWN BOX. It is the entire risk, and it is the one
                      * thing a cashflow figure can never show — because there is no repayment to show.
                      */}
                    <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white p-4">
                        <p className="mb-1.5 font-bold text-gray-800">The part that is easy to miss</p>
                        <p className="text-sm">
                            <strong>No repayments does not mean no cost.</strong> Because nothing is paid along the way,
                            the interest is charged on the interest. {isHeas
                                ? 'At around 4% a debt roughly doubles every eighteen years.'
                                : 'At around 8% a debt roughly doubles every nine years.'}{' '}
                            None of that appears in your yearly cashflow — it appears in what is left at the end. A plan
                            that looks comfortable year to year can still be the most expensive one on the table.
                        </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                        <p className="mb-1.5 font-bold text-gray-800">What you should know</p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            <li>
                                <strong>It comes out of the house.</strong> The debt is settled from the sale proceeds,
                                so it directly reduces what your family inherits — and it grows every year the loan runs.
                            </li>
                            <li>
                                <strong>You cannot owe more than the home is worth.</strong> Australian law requires a{' '}
                                <em>no negative equity guarantee</em> on {isHeas ? 'this scheme' : 'reverse mortgages'},
                                so neither you nor your estate can be pursued for a shortfall. This is a genuine
                                protection — but it protects against the worst case, not against the cost.
                            </li>
                            <li>
                                <strong>The home usually has to be sold to repay it.</strong> If you want the house kept
                                in the family, someone has to find the money to clear the loan first.
                            </li>
                            <li>
                                <strong>Anyone else living there.</strong> A partner or family member in the home needs
                                to be named on the loan, or their right to stay can end when yours does. Check this
                                before signing anything.
                            </li>
                            {!isHeas && (
                                <li>
                                    <strong>Rates and fees.</strong> The rate is usually variable, so the debt can grow
                                    faster than modelled here. There are establishment, valuation and legal costs, and
                                    sometimes ongoing fees.
                                </li>
                            )}
                            <li>
                                <strong>Centrelink.</strong> The home is exempt, but borrowed money that simply sits in
                                the bank is counted and deemed. Money that goes straight into an accommodation deposit is
                                exempt, which is what this strategy relies on.
                            </li>
                            {!isHeas && (
                                <li>
                                    <strong>You must get independent legal advice.</strong> That is a legal requirement
                                    for a reverse mortgage in Australia, not a formality — take it seriously and take it
                                    early.
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-white p-4">
                        <p className="mb-1.5 font-bold text-gray-800">Why it can still be the right answer</p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            <li>
                                <strong>The home is kept.</strong> Nobody has to sell in a hurry, and the property keeps
                                growing in value — which can outrun the interest.
                            </li>
                            <li>
                                <strong>It pays a refundable deposit.</strong> Money in an accommodation deposit is
                                exempt from the Age Pension assets test, removes that much daily accommodation payment,
                                and is refunded when care ends.
                            </li>
                            <li>
                                <strong>It buys time.</strong> Sometimes the choice is between borrowing and selling the
                                family home in the middle of a health crisis.
                            </li>
                        </ul>
                    </div>

                    <p className="text-sm text-slate-600">
                        Whether this leaves the family ahead depends on the room price, the interest rate, how fast the
                        home grows in value, and how long the stay is. Those are exactly what the projection here
                        compares — change the length of stay and watch which strategy wins.
                    </p>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default EquityReleaseTerms;
